import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { HeartRain } from "./HeartRain";
import { useI18n } from "@/context/LanguageContext";

const CopyRow = ({ label, value, testId, copyText, copiedText }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.setAttribute("readonly", "");
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, value.length);
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch (err) {
      console.error("Copy fallback failed", err);
    }
  };
  return (
    <div data-testid={testId} className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-5 border-b border-[#141518]/15 last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base text-[#F0311B]">{label}</div>
        <div className="font-display font-semibold text-base sm:text-xl md:text-2xl mt-1 break-all text-[#141518]">{value}</div>
      </div>
      <button
        onClick={onCopy}
        data-testid={`${testId}-copy`}
        className="self-start md:self-auto inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-[#141518] text-xs uppercase tracking-[0.2em] font-bold shadow-hard-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#141518] hover:bg-[#7AD5DC] transition-all bg-white text-[#141518] whitespace-nowrap"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? copiedText : copyText}
      </button>
    </div>
  );
};

export const Gift = () => {
  const { content, t } = useI18n();
  const g = t.gift;
  const gift = content.gift;
  return (
    <section id="regal" data-testid="gift-section" className="relative overflow-hidden bg-[#FFF7F1] py-16 md:py-36 border-y-2 border-[#141518]">
      <HeartRain density={55} />
      <div className="relative z-20 max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-12 gap-8 md:gap-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="col-span-12 md:col-span-5">
          <div className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base text-[#F0311B]">{g.eyebrow}</div>
          <h2 className="mt-4 font-display font-bold text-[13vw] sm:text-6xl md:text-7xl leading-[0.9]">
            {g.titleA} <br />
            <span className="italic">{g.titleB}</span>
          </h2>
          <p className="mt-6 text-[#5A5A5F] text-base md:text-lg leading-relaxed">{gift.intro}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="col-span-12 md:col-span-7 bg-white border-2 border-[#141518] shadow-hard p-6 md:p-10">
          <CopyRow label={g.lblIban} value={gift.iban} testId="gift-iban" copyText={g.copy} copiedText={g.copied} />
          <CopyRow label={g.lblRevolut} value={gift.revolut} testId="gift-revolut" copyText={g.copy} copiedText={g.copied} />
          <div className="py-5">
            <div className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base text-[#F0311B]">{g.lblCash}</div>
            <div className="font-display font-semibold text-xl md:text-2xl mt-1 text-[#141518]">{gift.cash}</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
